import { type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../../components/ui/states';
import { dashboardApi, dashboardKeys } from '../../features/dashboard/api';
import { seesPeopleData } from '../../features/performance/visibility';
import { AnomalyPanel } from '../../features/velocity/components/anomaly-panel';
import { VelocityPanel } from '../../features/velocity/components/velocity-panel';

export const Route = createFileRoute('/_app/velocity')({
  component: VelocityPage,
});

/**
 * Hiệu chỉnh ước lượng — APM-1001, thu hẹp ở APM-PERF2-a và lần nữa 2026-09-22.
 *
 * Trang này trả lời câu của **người lập kế hoạch**: loại việc này, công nghệ
 * này, giao cho ai thì mất bao lâu. Hồ sơ hiệu suất cá nhân đã chuyển sang
 * `/performance` — xem docblock của `VelocityPanel` về việc vì sao hai thứ ấy
 * không thuộc cùng một trang.
 *
 * ## ⚠️ Chỉ trưởng nhóm · DIRECTOR · quản trị tổ chức
 *
 * Người dùng chốt 2026-09-22. Trước đó trang mở cho tất cả với lý lẽ "ai cũng
 * xem được velocity của chính mình" — lý lẽ ấy không đứng được trước một câu
 * hỏi đơn giản: một nhân viên đọc "loại việc này giao cho ai thì mất bao lâu"
 * để làm gì? Họ không giao việc. Thứ nói về chính họ đã nằm ở `/performance`,
 * đầy đủ hơn và có giải thích.
 *
 * Cửa ở đây **và** ở thanh điều hướng, cùng đọc `seesPeopleData`: ẩn mục menu
 * mà để nguyên route thì một đường dẫn đã đánh dấu trang vẫn mở ra bảng, và
 * lúc ấy cái menu chỉ đang giấu chứ không đang chặn.
 */
function VelocityPage(): ReactNode {
  const { t } = useTranslation();
  const abilities = useQuery({
    queryKey: dashboardKeys.abilities,
    queryFn: dashboardApi.abilities,
  });

  if (abilities.isPending) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-medium">{t('velocity.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('velocity.subtitle')}</p>
      </header>

      {seesPeopleData(abilities.data) ? (
        <>
          <VelocityPanel />
          <AnomalyPanel />
        </>
      ) : (
        /* Nói ra **vì sao** và chỉ đi đâu, thay vì một trang trắng: người tới
           đây bằng một đường dẫn cũ cần biết thứ họ tìm nay nằm ở chỗ khác. */
        <p className="rounded-DEFAULT border border-border p-4 text-sm text-muted-foreground">
          {t('velocity.restricted')}
        </p>
      )}
    </div>
  );
}
